import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle, Sparkles, ChevronDown, ChevronRight } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import Seo, { SITE_URL } from '../components/Seo';
import { KEYWORD_PAGES } from '../data/keywordPages';
import { COMPETITORS } from '../data/competitors';

function NotFound({ slug }) {
  return (
    <div className="min-h-screen bg-[#0a0b0f] text-white">
      <Seo title="Page not found" description="Page not found" path={`/${slug}`} noIndex />
      <PublicNav />
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        <h1 className="text-3xl font-bold mb-3">Page not found</h1>
        <Link to="/" className="text-amber-400 hover:text-amber-300 font-semibold">← Home</Link>
      </div>
    </div>
  );
}

function FaqItem({ q, a, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="rounded-xl border border-dark-700/50 bg-dark-800/40">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left flex items-center justify-between gap-3 px-5 py-4"
      >
        <span className="text-base font-semibold text-white">{q}</span>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 text-sm text-gray-300 leading-relaxed">
          {a}
        </div>
      )}
    </div>
  );
}

export default function KeywordLandingPage() {
  const { slug: routeSlug } = useParams();
  // For static routes /best-crypto-trading-bot, /dex-trading-bot etc., useParams returns
  // an empty slug. We read the slug from the path instead — set via the route.
  const pathname = typeof window !== 'undefined' ? window.location.pathname.replace(/^\//, '') : '';
  const slug = routeSlug || pathname.split('/')[0];

  const page = KEYWORD_PAGES[slug];
  if (!page) return <NotFound slug={slug} />;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/${page.slug}` },
      headline: page.h1,
      description: page.metaDescription,
      author: { '@type': 'Organization', name: 'haythix' },
      publisher: {
        '@type': 'Organization',
        name: 'haythix',
        logo: { '@type': 'ImageObject', url: `${SITE_URL}/favicon.png` },
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: page.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: page.seoTitle, item: `${SITE_URL}/${page.slug}` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#0a0b0f] text-white">
      <Seo
        title={page.seoTitle}
        description={page.metaDescription}
        path={`/${page.slug}`}
        jsonLd={jsonLd}
        type="article"
      />
      <PublicNav />

      <section className="max-w-5xl mx-auto px-6 pt-12 pb-8">
        <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-5">{page.h1}</h1>
        <p className="text-gray-400 text-lg md:text-xl max-w-3xl mb-6">{page.intro}</p>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-dark-900 text-sm font-bold transition-all hover:-translate-y-0.5 shadow-lg shadow-amber-500/20"
          >
            Try free for 14 days <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/features"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-dark-600 hover:border-dark-500 text-gray-300 hover:text-white text-sm font-semibold transition-colors"
          >
            See all features
          </Link>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-10">
        <h2 className="text-2xl font-bold mb-6">What makes haythix different</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {page.pillarPoints.map((p, i) => (
            <div key={i} className="rounded-2xl border border-dark-700/50 bg-dark-800/40 p-6">
              <div className="flex items-start gap-3 mb-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mt-0.5">
                  <Sparkles className="w-4 h-4 text-amber-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">{p.title}</h3>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-10">
        <h2 className="text-2xl font-bold mb-6">Frequently asked questions</h2>
        <div className="space-y-3">
          {page.faqs.map((f, i) => (
            <FaqItem key={i} q={f.q} a={f.a} defaultOpen={i === 0} />
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-12">
        <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/5 via-dark-800 to-dark-900 p-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Ready to try haythix?</h2>
          <p className="text-gray-400 mb-6 max-w-xl mx-auto">14-day free trial, all features unlocked, no card required.</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-dark-900 text-sm font-bold transition-all hover:-translate-y-0.5 shadow-lg shadow-amber-500/20"
            >
              Create free account <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-dark-600 hover:border-dark-500 text-gray-300 hover:text-white text-sm font-semibold transition-colors"
            >
              See all pricing
            </Link>
          </div>
        </div>
      </section>

      {Array.isArray(page.relatedComparisons) && page.relatedComparisons.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 py-10 border-t border-dark-700/40">
          <p className="text-xs uppercase tracking-widest text-amber-400/80 mb-4">Compare to competitors</p>
          <div className="flex flex-wrap gap-2">
            {page.relatedComparisons.map((compSlug) => {
              const c = COMPETITORS[compSlug];
              if (!c) return null;
              return (
                <Link
                  key={compSlug}
                  to={`/vs/${compSlug}`}
                  className="text-xs px-3 py-1.5 rounded-full border border-dark-700 hover:border-amber-500/40 text-gray-400 hover:text-amber-300 transition-colors"
                >
                  haythix vs {c.name}
                </Link>
              );
            })}
          </div>
        </section>
      )}

      <PublicFooter />
    </div>
  );
}
